"use client"

import { Popover, Transition } from "@headlessui/react"
import { ChevronDownIcon } from "@heroicons/react/24/solid"
import { type FC, Fragment, useEffect, useState } from "react"
import { NavLink, useLocation } from "react-router-dom"
import NcImage from "shared/NcImage/NcImage"

// <--- NavItemType --->
export interface MegamenuItem {
  id: string
  image: string
  title: string
  items: NavItemType[]
}
export interface NavItemType {
  id: string
  name: string
  isNew?: boolean
  href: string
  targetBlank?: boolean
  children?: NavItemType[]
  megaMenu?: MegamenuItem[]
  type?: "dropdown" | "megaMenu" | "none"
}

export interface NavigationItemProps {
  menuItem: NavItemType
}

type NavigationItemWithRouterProps = NavigationItemProps

const NavigationItem: FC<NavigationItemWithRouterProps> = ({ menuItem }) => {
  const [menuCurrentHovers, setMenuCurrentHovers] = useState<string[]>([])

  // CLOSE ALL MENU OPENING WHEN CHANGE HISTORY
  const locationPathName = useLocation().pathname
  useEffect(() => {
    setMenuCurrentHovers([])
  }, [locationPathName])

  const onMouseEnterMenu = (id: string) => {
    setMenuCurrentHovers((state) => [...state, id])
  }

  const onMouseLeaveMenu = (id: string) => {
    setMenuCurrentHovers((state) => {
      return state.filter((item, index) => {
        return item !== id && index < state.indexOf(id)
      })
    })
  }

  // ===================== MENU MEGAMENU =====================
  const renderMegaMenu = (menu: NavItemType) => {
    const isHover = menuCurrentHovers.includes(menu.id)

    const isFull = menu.megaMenu && menu.megaMenu?.length > 3
    const classPopover = isFull ? "menu-megamenu--large" : "menu-megamenu--small relative"
    const classPanel = isFull ? "left-0" : "-translate-x-1/2 left-1/2"

    return (
      <Popover
        as="li"
        className={`menu-item menu-megamenu ${classPopover}`}
        onMouseEnter={() => onMouseEnterMenu(menu.id)}
        onMouseLeave={() => onMouseLeaveMenu(menu.id)}
      >
        {() => (
          <>
            <div>{renderMainItem(menu)}</div>
            <Transition
              as={Fragment}
              show={isHover}
              enter="transition ease-out duration-150"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel
                static
                className={`nc-will-change-transform sub-menu absolute transform z-10 w-screen max-w-sm px-4 pt-3 sm:px-0 lg:max-w-max ${classPanel}`}
              >
                <div className="overflow-hidden rounded-lg shadow-2xl ring-1 ring-amber-200 dark:ring-amber-900/50 ring-opacity-20 text-sm">
                  <div
                    className={`relative bg-gradient-to-br from-slate-50 to-amber-50 dark:from-slate-900 dark:to-amber-950/30 px-3 py-6 grid gap-1 grid-cols-${menu.megaMenu?.length} border-b border-amber-200/30 dark:border-amber-800/30`}
                  >
                    {menu.megaMenu?.map((item) => (
                      <div key={item.id} className="group">
                        <div className="px-2 relative overflow-hidden rounded-lg">
                          <div className="absolute inset-0 bg-gradient-to-t from-amber-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          <NcImage
                            containerClassName="w-36 h-24 rounded-lg overflow-hidden relative flex"
                            src={item.image}
                          />
                        </div>
                        <p className="font-semibold text-amber-900 dark:text-amber-200 py-1 px-2 my-2 text-center">
                          {item.title}
                        </p>
                        <ul className="grid space-y-1">{item.items.map(renderMegaMenuNavlink)}</ul>
                      </div>
                    ))}
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    )
  }

  const renderMegaMenuNavlink = (item: NavItemType) => {
    return (
      <li key={item.id}>
        <NavLink
          end
          target={item.targetBlank ? "_blank" : undefined}
          rel="noopener noreferrer"
          className={({ isActive }) =>
            `inline-flex items-center py-2 px-3 rounded-md hover:text-amber-700 hover:bg-amber-100/50 dark:hover:bg-amber-900/30 dark:hover:text-amber-300 font-medium text-amber-900 dark:text-amber-200 transition-colors duration-200 ${
              isActive ? "bg-amber-100/60 dark:bg-amber-900/40 font-semibold" : ""
            }`
          }
          to={item.href || ""}
        >
          {item.name}
        </NavLink>
      </li>
    )
  }

  // ===================== MENU DROPDOWN =====================
  const renderDropdownMenu = (menuDropdown: NavItemType) => {
    const isHover = menuCurrentHovers.includes(menuDropdown.id)
    return (
      <Popover
        as="li"
        className={`menu-item menu-dropdown relative ${menuDropdown.isNew ? "menuIsNew_lv1" : ""}`}
        onMouseEnter={() => onMouseEnterMenu(menuDropdown.id)}
        onMouseLeave={() => onMouseLeaveMenu(menuDropdown.id)}
      >
        {() => (
          <>
            <div>{renderMainItem(menuDropdown)}</div>
            <Transition
              as={Fragment}
              show={isHover}
              enter="transition ease-out duration-150 "
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel
                static
                className="sub-menu nc-will-change-transform absolute transform z-10 w-56 pt-3 left-0"
              >
                <ul className="rounded-lg shadow-2xl ring-1 ring-amber-200 dark:ring-amber-800/50 ring-opacity-20 text-sm relative bg-gradient-to-br from-slate-50 to-amber-50 dark:from-slate-900 dark:to-amber-950/20 py-4 grid space-y-1 border-t-2 border-amber-300/50 dark:border-amber-700/50">
                  {menuDropdown.children?.map((i) => {
                    if (i.type) {
                      return renderDropdownMenuNavlinkHasChild(i)
                    } else {
                      return (
                        <li key={i.id} className={`px-2 ${i.isNew ? "menuIsNew" : ""}`}>
                          {renderDropdownMenuNavlink(i)}
                        </li>
                      )
                    }
                  })}
                </ul>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    )
  }

  const renderDropdownMenuNavlinkHasChild = (item: NavItemType) => {
    const isHover = menuCurrentHovers.includes(item.id)
    return (
      <Popover
        as="li"
        key={item.id}
        className="menu-item menu-dropdown relative px-2"
        onMouseEnter={() => onMouseEnterMenu(item.id)}
        onMouseLeave={() => onMouseLeaveMenu(item.id)}
      >
        {() => (
          <>
            <div>{renderDropdownMenuNavlink(item)}</div>
            <Transition
              as={Fragment}
              show={isHover}
              enter="transition ease-out duration-150"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel static className="sub-menu absolute z-10 w-56 left-full pl-2 top-0">
                <ul className="rounded-lg shadow-2xl ring-1 ring-amber-200 dark:ring-amber-800/50 ring-opacity-20 text-sm relative bg-gradient-to-br from-slate-50 to-amber-50 dark:from-slate-900 dark:to-amber-950/20 py-4 grid space-y-1 border-t-2 border-amber-300/50 dark:border-amber-700/50">
                  {item.children?.map((i) => {
                    if (i.type) {
                      return renderDropdownMenuNavlinkHasChild(i)
                    } else {
                      return (
                        <li key={i.id} className="px-2">
                          {renderDropdownMenuNavlink(i)}
                        </li>
                      )
                    }
                  })}
                </ul>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    )
  }

  const renderDropdownMenuNavlink = (item: NavItemType) => {
    return (
      <NavLink
        end
        target={item.targetBlank ? "_blank" : undefined}
        rel="noopener noreferrer"
        className={({ isActive }) =>
          `flex items-center font-medium text-amber-900 dark:text-amber-200 py-2 px-4 rounded-md hover:text-amber-700 hover:bg-amber-100/50 dark:hover:bg-amber-900/30 dark:hover:text-amber-300 transition-all duration-200 ${
            isActive ? "!font-semibold bg-amber-100/60 dark:bg-amber-900/40 dark:!text-amber-100" : ""
          }`
        }
        to={item.href || ""}
      >
        {item.name}
        {item.type && (
          <ChevronDownIcon className="ml-2 h-4 w-4 text-amber-700 dark:text-amber-400" aria-hidden="true" />
        )}
      </NavLink>
    )
  }

  // ===================== MENU MAIN MENU =====================
  const renderMainItem = (item: NavItemType) => {
    return (
      <NavLink
        end
        target={item.targetBlank ? "_blank" : undefined}
        rel="noopener noreferrer"
        className={({ isActive }) =>
          `inline-flex items-center text-sm xl:text-base font-semibold dark:text-amber-200 py-2 px-4 xl:px-5 rounded-lg hover:text-amber-700 hover:bg-amber-100/40 dark:hover:bg-amber-900/30 dark:hover:text-amber-300 transition-all duration-200 ${
            isActive
              ? "!font-bold bg-gradient-to-r from-amber-400 to-amber-500 dark:from-amber-600 dark:to-amber-700 dark:!text-white text-white shadow-lg shadow-amber-400/30"
              : "text-amber-900"
          }`
        }
        to={item.href || "/"}
      >
        {item.name}
        {item.type && (
          <ChevronDownIcon className="ml-1 -mr-1 h-4 w-4 text-amber-700 dark:text-amber-400" aria-hidden="true" />
        )}
      </NavLink>
    )
  }

  switch (menuItem.type) {
    case "megaMenu":
      return renderMegaMenu(menuItem)
    case "dropdown":
      return renderDropdownMenu(menuItem)
    default:
      return <li className="menu-item">{renderMainItem(menuItem)}</li>
  }
}

export default NavigationItem
